import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { CircularProgress } from '@material-ui/core';

import * as ls from 'src/lib/localData';
import { PERFORMANCE_EXTRACTION_STATUS, useCurrentWorkspaceId } from 'src/store';
import { alignChannelWithBackend, getChannelPerformanceLabel } from 'src/data/channel';
import { useErrorHandler } from 'src/hooks';
import { variationsForBenchmark } from 'src/graphql/performance';
import { RESOURCE_CHANNELS, getResourceDocumentLabel } from 'src/data/performance';
import { useAccountsDataExtractionNavigationPopup } from 'src/services/popup';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';
import { useSubscribeForPubsub } from './useSubscribeForPubsub';
import { useIsApplicationModeInfinity } from 'src/services';

const alignChannel = (channel) => alignChannelWithBackend(channel);

export const DataExtractionIndicator = () => {
  const styles = useStyles();

  const currentWorkspaceId = useCurrentWorkspaceId();
  const openAccountsDataExtractionNavigationPopup = useAccountsDataExtractionNavigationPopup();
  const handleError = useErrorHandler();
  const isApplicationModeInfinity = useIsApplicationModeInfinity();

  const [variationBenchmarkDelayProcessing, setVariationBenchmarkDelayProcessing] = useState(false);

  const { avgCompletionPercent, extractionStatus } = useViewState();

  const anyChannelInProgress = Object.keys(extractionStatus).filter(
    (key) =>
      extractionStatus?.[key] === PERFORMANCE_EXTRACTION_STATUS.running &&
      !RESOURCE_CHANNELS.includes(key)
  );

  const getChannelLabel = (channel) =>
    getChannelPerformanceLabel(channel) || getResourceDocumentLabel(channel);

  const getTitle = (showDelayedMessage, channelIsDone) => {
    if (showDelayedMessage) {
      return `Saving ${getChannelLabel(channelIsDone)} data with Anyword...`;
    }

    const channelLabel =
      anyChannelInProgress.length === 1
        ? getChannelLabel(anyChannelInProgress[0])
        : `${anyChannelInProgress.length} channels`;

    const progress = avgCompletionPercent[anyChannelInProgress[anyChannelInProgress.length - 1]];

    const actionLabel = progress < 34 ? 'Connecting' : progress < 67 ? 'Extracting' : 'Saving';

    return `${actionLabel} ${channelLabel} data with Anyword...`;
  };

  const channelsInProgressRef = useRef([]);
  const lastChannelInProgress = useRef(null);
  const [isInsightsPanelOpen, setIsInsightsPanelOpen] = useState(false);

  const projectId = ls.benchmarkProjectId.read();
  const variationId = ls.benchmarkVariationId.read();

  useEffect(() => {
    const getVariationForBenchmark = async (channelIsDone) => {
      if (RESOURCE_CHANNELS.includes(channelIsDone) || isApplicationModeInfinity) {
        setVariationBenchmarkDelayProcessing(false);
        return;
      }
      try {
        const variation = await variationsForBenchmark({
          workspaceId: currentWorkspaceId,
          dataSource: alignChannel(channelIsDone),
        });
        openAccountsDataExtractionNavigationPopup({
          channel: channelIsDone,
          projectId,
          variationId,
          variation,
        });
      } catch (error) {
        handleError(error);
      } finally {
        setVariationBenchmarkDelayProcessing(false);
      }
    };

    if (
      !(anyChannelInProgress.length || channelsInProgressRef.current.length) ||
      !currentWorkspaceId
    ) {
      return;
    }
    lastChannelInProgress.current = channelsInProgressRef.current.find(
      (channelInProgress) => !anyChannelInProgress.includes(channelInProgress)
    );

    const isLastChannelExtractionDone =
      extractionStatus[lastChannelInProgress.current] === PERFORMANCE_EXTRACTION_STATUS.done;

    if (lastChannelInProgress.current && isLastChannelExtractionDone) {
      setVariationBenchmarkDelayProcessing(true);
      getVariationForBenchmark(lastChannelInProgress.current);
      channelsInProgressRef.current = channelsInProgressRef.current.filter(
        (prevChannelInProgress) => prevChannelInProgress !== lastChannelInProgress.current
      );
      ls.benchmarkProjectId.remove();
      ls.benchmarkVariationId.remove();
    }
    if (anyChannelInProgress.length) {
      // we don't want to override channelsInProgressRef if anyChannelInProgress is empty
      channelsInProgressRef.current = anyChannelInProgress;
    }
  }, [
    currentWorkspaceId,
    channelsInProgressRef,
    openAccountsDataExtractionNavigationPopup,
    anyChannelInProgress,
    extractionStatus,
    projectId,
    variationId,
    handleError,
    isApplicationModeInfinity,
  ]);

  useSubscribeForPubsub(setIsInsightsPanelOpen);

  if (!currentWorkspaceId || (!anyChannelInProgress.length && !variationBenchmarkDelayProcessing)) {
    return null;
  }

  return (
    <div
      className={cx(styles.root, {
        [styles.isInsightsPanelOpen]: isInsightsPanelOpen,
      })}
    >
      <CircularProgress
        classes={{ indeterminate: styles.loader }}
        size={16}
      />
      <CustomTypography
        className={styles.title}
        variant={TYPOGRAPHY_VARIANT.regular14}
      >
        {getTitle(
          variationBenchmarkDelayProcessing && !anyChannelInProgress.length,
          lastChannelInProgress.current
        )}
      </CustomTypography>
    </div>
  );
};
